/**
 * Lesson Booking Form Handler
 * Handles form submission for booking training lessons
 */

class LessonBookingForm {
  constructor(config) {
    this.formId = config.formId;
    this.action = config.action;
    this.successMessage = config.successMessage || 'Booking successful!';
    this.errorMessage = config.errorMessage || 'An error occurred. Please try again.';
    this.translations = config.translations || {
      errors: {}
    };
    this.form = null;
    this.init();
  }

  translateFieldName(fieldName) {
    if (this.translations.fieldNames && this.translations.fieldNames[fieldName]) {
      return this.translations.fieldNames[fieldName];
    }
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  translateError(key, defaultValue, placeholders) {
    let text = this.translations.errors[key] || defaultValue;
    if (placeholders && typeof text === 'string') {
      Object.keys(placeholders).forEach((k) => {
        text = text.split('%{' + k + '}').join(String(placeholders[k]));
      });
    }
    return text;
  }

  sanitizeValidationMessage(msg) {
    if (typeof msg !== 'string') return msg;
    return msg
      .replace(/^The /i, '')
      .replace(/ field is required\.?$/i, 'is required.')
      .replace(/ field /i, ' ')
      .replace(/ must be /i, 'must be ')
      .replace(/ must have /i, 'must have ')
      .replace(/ is invalid\.?$/i, 'is invalid.')
      .trim();
  }

  init() {
    this.form = document.getElementById(this.formId);
    if (!this.form) {
      return;
    }

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit(e);
    });

    if (!this.form.getAttribute('data-original-action') && this.form.action !== 'javascript:void(0);') {
      this.form.setAttribute('data-original-action', this.form.action || '');
    }
    if (this.form.action !== 'javascript:void(0);') {
      this.form.action = 'javascript:void(0);';
    }
    this.form.setAttribute('data-lesson-booking-initialized', 'true');
  }

  handleSubmit() {
    const submitButton = this.form.querySelector('[type="submit"]');
    const resultDiv = this.form.querySelector('.result');

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('loading');
    }

    const formData = new FormData(this.form);
    const data = {};

    for (let [key, value] of formData.entries()) {
      if (key === 'brings_own_device' || key === 'not_apple_user') {
        data[key] = value === 'true' || value === 'on' || value === true || value === '1' || value === 1;
      } else if (key !== 'gdpr') {
        data[key] = value;
      }
    }

    const bringsOwnDeviceCheckbox = this.form.querySelector('input[name="brings_own_device"]');
    const notAppleUserCheckbox = this.form.querySelector('input[name="not_apple_user"]');

    if (bringsOwnDeviceCheckbox) {
      data.brings_own_device = bringsOwnDeviceCheckbox.checked;
    } else if (!Object.prototype.hasOwnProperty.call(data, 'brings_own_device')) {
      data.brings_own_device = false;
    }

    if (notAppleUserCheckbox) {
      data.not_apple_user = notAppleUserCheckbox.checked;
    } else if (!Object.prototype.hasOwnProperty.call(data, 'not_apple_user')) {
      data.not_apple_user = false;
    }

    data.brings_own_device = Boolean(data.brings_own_device);
    data.not_apple_user = Boolean(data.not_apple_user);

    if (!data.lesson_id) {
      const lessonIdInput = document.getElementById('book-lesson-id');
      if (lessonIdInput) {
        data.lesson_id = lessonIdInput.value;
      }
    }

    if (!data.shopify_customer_id) {
      const customerIdInput = document.getElementById('book-shopify-customer-id');
      if (customerIdInput) {
        data.shopify_customer_id = customerIdInput.value;
      }
    }

    const payload = {
      lesson_id: String(data.lesson_id || '').trim(),
      first_name: String(data.first_name || '').trim(),
      last_name: String(data.last_name || '').trim(),
      email: String(data.email || '').trim(),
      phone: String(data.phone || '').trim(),
      brings_own_device: data.brings_own_device,
      not_apple_user: data.not_apple_user
    };

    if (data.shopify_customer_id && String(data.shopify_customer_id).trim() !== '') {
      payload.shopify_customer_id = String(data.shopify_customer_id).trim();
    }

    if (!payload.lesson_id) {
      this.handleError(
        {
          message: this.translateError('invalid_lesson', 'Invalid lesson ID'),
          error: 'validation'
        },
        resultDiv
      );
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
      }
      return;
    }

    fetch(this.action, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload),
      redirect: 'manual'
    })
      .then((response) => this.handleResponse(response))
      .then((resData) => this.handleSuccess(resData, resultDiv))
      .catch((error) => this.handleError(error, resultDiv))
      .finally(() => {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('loading');
        }
      });
  }

  handleResponse(response) {
    const contentType = response.headers.get('content-type');

    if (response.status >= 300 && response.status < 400) {
      return Promise.reject({
        error: 'redirect',
        status: response.status,
        message: this.translateError(
          'redirect',
          'Request was redirected. This may indicate an authentication issue.'
        )
      });
    }

    if (!response.ok) {
      if (response.status === 422) {
        return response.text().then((text) => {
          try {
            const err = JSON.parse(text);
            return Promise.reject({
              error: 'validation',
              status: 422,
              errors: err.errors || {},
              message: err.message || this.translateError('validation_failed', 'Validation failed')
            });
          } catch (parseErr) {
            return Promise.reject({
              error: 'validation',
              status: 422,
              message: text || this.translateError('validation_failed', 'Validation failed')
            });
          }
        });
      }

      return response.text().then((text) => {
        const lower = text.trim().toLowerCase();
        if (lower.startsWith('<!doctype') || lower.startsWith('<html')) {
          return Promise.reject({
            error: 'server_error',
            status: response.status,
            isHtml: true,
            message: this.translateError('server_error_status', 'Server error (%{status})', {
              status: response.status
            })
          });
        }

        try {
          const err = JSON.parse(text);
          return Promise.reject({
            error: 'http_error',
            status: response.status,
            ...err
          });
        } catch (parseErr) {
          return Promise.reject({
            error: 'http_error',
            status: response.status,
            message: text.substring(0, 500) || response.statusText
          });
        }
      });
    }

    return response.text().then((text) => {
      const lower = text.trim().toLowerCase();
      if (lower.startsWith('<!doctype') || lower.startsWith('<html')) {
        return Promise.reject({
          error: 'server_error',
          status: response.status,
          isHtml: true,
          message: this.translateError(
            'html_instead_of_json',
            'The server returned a page instead of data. Please try again later.'
          )
        });
      }

      if (contentType && contentType.includes('application/json')) {
        try {
          return JSON.parse(text);
        } catch (parseErr) {
          return { message: text, raw: true };
        }
      }
      return { message: text, raw: true };
    });
  }

  handleSuccess(data, resultDiv) {
    if (resultDiv) {
      resultDiv.innerHTML = '<div class="note note--success"><p>' + this.successMessage + '</p></div>';
    }

    this.form.reset();

    if (resultDiv) {
      resultDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  handleError(error, resultDiv) {
    if (!resultDiv) {
      return;
    }

    let errorMessage = this.errorMessage;
    let bodyHtml = '';

    if (error.error === 'validation' || error.status === 422) {
      errorMessage = '⚠️ ' + this.translateError('validation_title', 'Validation error');
      if (error.errors && Object.keys(error.errors).length > 0) {
        bodyHtml =
          '<p style="font-size: 14px; margin: 8px 0 0;">' +
          this.translateError('validation', 'Please fix the following errors:') +
          '</p>' +
          '<ul style="margin: 10px 0 0; padding-left: 20px; font-size: 14px;">';
        Object.keys(error.errors).forEach((field) => {
          const fieldName = this.translateFieldName(field);
          error.errors[field].forEach((msg) => {
            const cleanMsg = this.sanitizeValidationMessage(msg);
            bodyHtml += `<li style="margin: 5px 0;"><strong>${fieldName}:</strong> ${cleanMsg}</li>`;
          });
        });
        bodyHtml += '</ul>';
      } else {
        bodyHtml =
          '<p style="margin-top: 8px;">' +
          (error.message || this.translateError('one_or_more_invalid', 'One or more fields are invalid.')) +
          '</p>';
      }
    } else if (error.error === 'redirect') {
      errorMessage = this.translateError(
        'redirect',
        'Request was redirected. This may indicate an authentication issue.'
      );
    } else if (error.error === 'server_error' || (error.status && error.status >= 500)) {
      errorMessage =
        error.message ||
        this.translateError('server_error_status', 'Server error (%{status})', {
          status: error.status || 500
        });
      bodyHtml =
        '<p style="font-size: 13px; margin-top: 10px; line-height: 1.6; color: #d32f2f;">' +
        this.translateError('server', 'A server error occurred. Please try again later.') +
        '</p>';
    } else if (error.status) {
      errorMessage = `${this.translateError('server_error_status', 'Server error (%{status})', {
        status: error.status
      })}: ${error.message || error.statusText || this.translateError('unknown_error', 'Unknown error')}`;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    resultDiv.innerHTML =
      '<div class="note note--error"><p><strong>' + errorMessage + '</strong></p>' + bodyHtml + '</div>';
    resultDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = LessonBookingForm;
}

window.LessonBookingForm = LessonBookingForm;

/**
 * Reads booking form strings from <script type="application/json" id="LessonBookingI18n-{formId}">.
 */
window.lessonBookingFormParseConfig = function lessonBookingFormParseConfig(formId) {
  const el = document.getElementById('LessonBookingI18n-' + formId);
  if (!el || !el.textContent || !el.textContent.trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(el.textContent);
    if (parsed && parsed.translations) {
      if (!parsed.translations.errors) parsed.translations.errors = {};
      if (!parsed.translations.fieldNames) parsed.translations.fieldNames = {};
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

(function () {
  function initLessonBookingForm(formId) {
    const form = document.getElementById(formId);
    if (!form || form.hasAttribute('data-lesson-booking-initialized')) {
      return;
    }

    if (!form.getAttribute('data-original-action')) {
      form.setAttribute('data-original-action', form.action || '');
    }

    let action = form.getAttribute('data-original-action');
    if (!action || action === 'javascript:void(0);') {
      action = 'https://' + window.location.host + '/apps/lab9/proxy/book-lesson';
    }

    form.action = 'javascript:void(0);';

    const i18n =
      typeof window.lessonBookingFormParseConfig === 'function'
        ? window.lessonBookingFormParseConfig(formId)
        : null;

    new window.LessonBookingForm({
      formId: formId,
      action: action,
      successMessage: (i18n && i18n.successMessage) || 'Booking successful!',
      errorMessage: (i18n && i18n.errorMessage) || 'An error occurred. Please try again.',
      translations: (i18n && i18n.translations) || { errors: {}, fieldNames: {} }
    });
  }

  function scanForms() {
    document.querySelectorAll('form[id^="bookLessonForm"]').forEach((f) => {
      initLessonBookingForm(f.id);
    });
    initLessonBookingForm('bookLessonForm');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanForms);
  } else {
    scanForms();
  }
})();
